import GridContainer from '@feature/GridContainer'
import GridItem from '@feature/GridItem'
import LoadingPopover from '@feature/LoadingPopover/LoadingPopover'
import ResultsGridPagination from '@feature/ResultsGridPagination'
import TermCard from '@feature/TermCard/TermCard'
import { GridProps, Typography } from '@mui/material'
import {
  OverridableComponent,
  OverridableTypeMap
} from '@mui/material/OverridableComponent'
import { selectIsFiltered, selectMainCategories } from 'app/selectors'
import useProducts from 'helpers/useProducts'
import React from 'react'
import { useSelector } from 'react-redux'
import NoResults from './NoResults'
import ResultsCount from './ResultsCount'
import { useSearchProductsQuery } from 'app/services/api'
import { selectProductSearchQuery } from 'app/selectors'
import { useAppSelector } from 'app/hooks'
import { queryUpdated, selectTotalCount } from 'app/results/resultsSlice'
import { useAppDispatch } from 'app/hooks'


type ResultsGridProps<T extends OverridableComponent<OverridableTypeMap> = any> = {
  EntityComp: T
} & GridProps

const ResultsGrid = ({ EntityComp, ...props }: ResultsGridProps) => {
  const dispatch = useAppDispatch()
  const isFiltered = useSelector(selectIsFiltered)
  const mainCategories = useSelector(selectMainCategories)

  const { products, resultsLoading, isLoading, isFetching } = useProducts(undefined, !isFiltered)
  const nResults = products.length

  const showLoading = isLoading || isFetching || resultsLoading
  const showNoResults = !resultsLoading && isFiltered && !nResults
  const showCategories = !isFiltered && mainCategories
  const showProducts = !showCategories
  
  const searchArgs = useAppSelector(selectProductSearchQuery)
  const { data } = useSearchProductsQuery(searchArgs ?? {})
  const prevTotal = useAppSelector(selectTotalCount)
  const newTotal = data?.products?.pageInfo?.offsetPagination?.total ?? 0

  if (products.length > 0 && prevTotal && newTotal && newTotal !== prevTotal) {
    dispatch(queryUpdated(newTotal))
  }

  return (
    <GridContainer rowSpacing={4} sx={{ opacity: showLoading ? 0.5 : 1 }} {...props}>
      {/* Progress indicator */}
      {showLoading && <LoadingPopover />}

      {/* If no results */}
      {showNoResults && (
        <GridItem>
          <NoResults />
        </GridItem>
      )}

      {/* Main category tiles */}
      {showCategories && (
        <>
          <GridItem>
            <Typography textAlign="center" marginY={1} whiteSpace="pre-line">{`
              Filter products or
              select a category below
            `}</Typography>
          </GridItem>
          {mainCategories.map((term) => (
            <TermCard key={term.id} term={term} component={GridItem} item alignItems={'stretch'} xs={6} md={4} />
          ))}
        </>
      )}
      {showProducts && !showNoResults && <ResultsCount />}

      {/* Products */}
      {showProducts && products.map((entity) => <EntityComp key={entity.id} entity={entity} component={GridItem} item xs={6} md={4} stickerType={entity.sticker} />)}

      {/* Product Pagination */}
      {showProducts && !showNoResults && <ResultsGridPagination />}
    </GridContainer>
  )
}

export default ResultsGrid
